/**
 * AutoRefreshToggle Component
 * 
 * A reusable toggle control for enabling/disabling auto-refresh functionality.
 * Displays a styled card with sync icon, status text, and switch control.
 * Used across multiple pages for consistent auto-refresh UX.
 */

import React from 'react';
import { Card, Space, Typography, Switch, theme } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export interface AutoRefreshToggleProps {
  /** Whether auto-refresh is currently enabled */
  autoRefresh: boolean;
  /** Callback when auto-refresh toggle is changed */
  onAutoRefreshChange: (enabled: boolean) => void;
  /** Whether data is currently being fetched */
  isFetching?: boolean;
  /** Last updated timestamp (formatted string, e.g., "HH:mm:ss") */
  lastUpdated?: string;
}

/**
 * Auto-refresh toggle card component
 * Shows sync icon, "Auto refresh" label, last updated time, and on/off switch
 */
export const AutoRefreshToggle: React.FC<AutoRefreshToggleProps> = ({
  autoRefresh,
  onAutoRefreshChange,
  isFetching = false,
  lastUpdated
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('common');

  return (
    <Card
      styles={{ body: { padding: '10px 16px' } }}
      style={{
        borderRadius: 8,
        border: `1px solid ${autoRefresh ? token.colorPrimaryBorder : token.colorBorder}`,
        backgroundColor: autoRefresh ? token.colorPrimaryBg : token.colorFillQuaternary,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
        transition: 'all 0.3s ease'
      }}
    >
      <Space size="middle">
        <SyncOutlined
          spin={autoRefresh && isFetching}
          style={{
            fontSize: 16,
            color: autoRefresh ? token.colorPrimary : token.colorTextSecondary,
            transition: 'color 0.3s ease'
          }}
        />
        <div>
          <Text
            strong
            style={{
              fontSize: 14,
              color: autoRefresh ? token.colorPrimary : token.colorTextSecondary,
              display: 'block'
            }}
          >
            {t('ui.autoRefresh.label')}
          </Text>
          {autoRefresh && lastUpdated && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('ui.autoRefresh.lastUpdated', { time: lastUpdated })}
            </Text>
          )}
        </div>
        <Switch
          checked={autoRefresh}
          onChange={onAutoRefreshChange}
          checkedChildren={t('ui.autoRefresh.on')}
          unCheckedChildren={t('ui.autoRefresh.off')}
        />
      </Space>
    </Card>
  );
};

export default AutoRefreshToggle;
